/**
 * Australian medicolegal vocabulary.
 *
 * Two jobs. First, replace United States institutional and clinical terms with
 * the Australian equivalent, because a report that says "emergency room" or
 * "physical therapist" reads as though it was written somewhere else. Second,
 * canonicalise the proper nouns that speech recognition reliably mangles:
 * WorkCover, AHPRA, Centrelink and the rest.
 *
 * Phrases are matched longest first, so "workers comp claim" resolves before
 * "workers comp".
 */
/** Statutory and institutional proper nouns, as they must appear. */
const INSTITUTIONS = [
    { from: 'work cover', to: 'WorkCover', literal: true },
    { from: 'workcover', to: 'WorkCover', literal: true },
    { from: 'work cover wa', to: 'WorkCover WA', literal: true },
    { from: 'workcover wa', to: 'WorkCover WA', literal: true },
    { from: 'work cover western australia', to: 'WorkCover WA', literal: true },
    { from: 'work safe', to: 'WorkSafe', literal: true },
    { from: 'worksafe', to: 'WorkSafe', literal: true },
    { from: 'ahpra', to: 'AHPRA', literal: true },
    { from: 'a h p r a', to: 'AHPRA', literal: true },
    { from: 'medicare', to: 'Medicare', literal: true },
    { from: 'centrelink', to: 'Centrelink', literal: true },
    { from: 'centre link', to: 'Centrelink', literal: true },
    { from: 'insurance commission of western australia', to: 'Insurance Commission of Western Australia', literal: true },
    { from: 'icwa', to: 'Insurance Commission of Western Australia', literal: true, confidence: 'likely' },
    { from: 'ama guides', to: 'AMA Guides', literal: true },
    { from: 'a m a guides', to: 'AMA Guides', literal: true },
    { from: 'royal perth hospital', to: 'Royal Perth Hospital', literal: true },
    { from: 'sir charles gairdner hospital', to: 'Sir Charles Gairdner Hospital', literal: true },
    { from: 'fiona stanley hospital', to: 'Fiona Stanley Hospital', literal: true },
    { from: 'joondalup health campus', to: 'Joondalup Health Campus', literal: true },
    { from: 'st john of god', to: 'St John of God', literal: true },
    { from: 'pbs', to: 'PBS', literal: true, confidence: 'likely' },
    { from: 'ndis', to: 'NDIS', literal: true },
    { from: 'racgp', to: 'RACGP', literal: true },
    { from: 'ranzcp', to: 'RANZCP', literal: true },
    { from: 'fracp', to: 'FRACP', literal: true },
    { from: 'fafoem', to: 'FAFOEM', literal: true },
    { from: 'fracs', to: 'FRACS', literal: true },
    { from: 'apia', to: 'APIA', literal: true },
    { from: 'ams', to: 'AMS', literal: true, confidence: 'likely', note: 'Approved Medical Specialist' },
];
/** United States clinical and legal terms that do not belong in an Australian report. */
const AMERICANISMS = [
    { from: 'emergency room', to: 'emergency department' },
    { from: 'the er', to: 'the emergency department' },
    { from: 'physical therapy', to: 'physiotherapy' },
    { from: 'physical therapist', to: 'physiotherapist' },
    { from: 'physical therapists', to: 'physiotherapists' },
    { from: 'occupational therapy', to: 'occupational therapy' },
    { from: 'primary care physician', to: 'general practitioner' },
    { from: 'family physician', to: 'general practitioner' },
    { from: 'family doctor', to: 'general practitioner' },
    { from: 'drug store', to: 'chemist' },
    { from: 'drugstore', to: 'chemist' },
    { from: 'pharmacy', to: 'pharmacy' },
    { from: 'gurney', to: 'trolley', confidence: 'likely' },
    { from: 'attending physician', to: 'treating doctor', confidence: 'likely' },
    { from: 'resident physician', to: 'registrar', confidence: 'likely' },
    { from: 'board certified', to: 'fellowship trained', confidence: 'likely' },
    { from: 'attorney', to: 'solicitor', confidence: 'likely', note: 'confirm whether solicitor or barrister is meant' },
    { from: 'attorneys', to: 'solicitors', confidence: 'likely' },
    { from: 'law suit', to: 'claim', confidence: 'likely' },
    { from: 'lawsuit', to: 'claim', confidence: 'likely' },
    { from: 'workmans comp', to: "workers' compensation" },
    { from: "workman's comp", to: "workers' compensation" },
    { from: 'workmans compensation', to: "workers' compensation" },
    { from: 'workers comp', to: "workers' compensation" },
    { from: 'workers compensation', to: "workers' compensation" },
    { from: 'disability insurance', to: 'income protection', confidence: 'likely' },
    { from: 'zip code', to: 'postcode' },
    { from: 'social security number', to: 'tax file number', confidence: 'likely' },
    { from: 'high school', to: 'high school' },
    { from: 'elementary school', to: 'primary school' },
    { from: 'freeway', to: 'freeway' },
    { from: 'sidewalk', to: 'footpath' },
    { from: 'truck driver', to: 'truck driver' },
    { from: 'semi truck', to: 'semi trailer' },
    { from: 'vacation', to: 'holiday', confidence: 'likely' },
    { from: 'gas station', to: 'service station' },
    { from: 'apartment', to: 'unit', confidence: 'likely' },
    { from: 'trash', to: 'rubbish' },
    { from: 'flashlight', to: 'torch' },
    { from: 'hood of the car', to: 'bonnet of the car' },
    { from: 'trunk of the car', to: 'boot of the car' },
];
/** Domain phrases with one correct written form. */
const DOMAIN = [
    { from: 'whole person impairment', to: 'whole person impairment' },
    { from: 'w p i', to: 'WPI', literal: true },
    { from: 'independent medical examination', to: 'independent medical examination' },
    { from: 'i m e', to: 'IME', literal: true },
    { from: 'ime', to: 'IME', literal: true },
    { from: 'm v a', to: 'MVA', literal: true },
    { from: 'motor vehicle accident', to: 'motor vehicle accident' },
    { from: 'permanent impairment', to: 'permanent impairment' },
    { from: 'maximum medical improvement', to: 'maximum medical improvement' },
    { from: 'm m i', to: 'MMI', literal: true },
    { from: 'pre injury duties', to: 'pre-injury duties' },
    { from: 'preinjury duties', to: 'pre-injury duties' },
    { from: 'return to work', to: 'return to work' },
    { from: 'r t w', to: 'RTW', literal: true },
    { from: 'fitness for work', to: 'fitness for work' },
    { from: 'f f w', to: 'FFW', literal: true },
    { from: 'certificate of capacity', to: 'certificate of capacity' },
    { from: 'letter of instruction', to: 'letter of instruction' },
    { from: 'l o i', to: 'LOI', literal: true },
    { from: 'range of motion', to: 'range of motion' },
    { from: 'r o m', to: 'ROM', literal: true },
    { from: 'activities of daily living', to: 'activities of daily living' },
    { from: 'a d ls', to: 'ADLs', literal: true },
    { from: 'expert witness code of conduct', to: 'Expert Witness Code of Conduct', literal: true },
    { from: 'federal court rules', to: 'Federal Court Rules', literal: true },
    { from: 'schedule of documents', to: 'Schedule of Documents', literal: true },
];
/** Anatomical and clinical shorthand that dictation produces. */
const CLINICAL_SHORTHAND = [
    { from: 'rt', to: 'right', confidence: 'likely' },
    { from: 'lt', to: 'left', confidence: 'likely' },
    { from: 'bilat', to: 'bilateral' },
    { from: 'c spine', to: 'cervical spine' },
    { from: 'c-spine', to: 'cervical spine' },
    { from: 't spine', to: 'thoracic spine' },
    { from: 't-spine', to: 'thoracic spine' },
    { from: 'l spine', to: 'lumbar spine' },
    { from: 'l-spine', to: 'lumbar spine' },
    { from: 'ls spine', to: 'lumbosacral spine' },
    { from: 'x ray', to: 'X-ray' },
    { from: 'xray', to: 'X-ray' },
    { from: 'x-ray', to: 'X-ray' },
    { from: 'm r i', to: 'MRI', literal: true },
    { from: 'c t scan', to: 'CT scan', literal: true },
    { from: 'ct scan', to: 'CT scan', literal: true },
    { from: 'ultra sound', to: 'ultrasound' },
    { from: 'e m g', to: 'EMG', literal: true },
    { from: 'n c s', to: 'nerve conduction study', literal: true },
    { from: 'g p', to: 'general practitioner' },
    { from: 'nsaid', to: 'NSAID', literal: true },
    { from: 'nsaids', to: 'NSAIDs', literal: true },
    { from: 'p r n', to: 'as required' },
    { from: 'prn', to: 'as required', confidence: 'likely' },
    { from: 'b d', to: 'twice daily', confidence: 'likely' },
    { from: 't d s', to: 'three times daily', confidence: 'likely' },
    { from: 'nocte', to: 'at night' },
    { from: 'mane', to: 'in the morning' },
    { from: 'crps', to: 'CRPS', literal: true },
    { from: 'complex regional pain syndrome', to: 'complex regional pain syndrome' },
    { from: 'ptsd', to: 'PTSD', literal: true },
    { from: 'p t s d', to: 'PTSD', literal: true },
    { from: 'dass 21', to: 'DASS-21', literal: true },
    { from: 'dass twenty one', to: 'DASS-21', literal: true },
    { from: 'moca', to: 'MoCA', literal: true },
    { from: 'womac', to: 'WOMAC', literal: true },
    { from: 'pirs', to: 'PIRS', literal: true },
    { from: 'gepic', to: 'GEPIC', literal: true },
];
export const VOCABULARY = [
    ...INSTITUTIONS,
    ...AMERICANISMS,
    ...DOMAIN,
    ...CLINICAL_SHORTHAND,
]
    .filter((e) => e.from !== e.to.toLowerCase() || e.literal)
    // Longest phrase first so multi-word entries win.
    .sort((a, b) => b.from.length - a.from.length);
/**
 * Build one alternation regex for the whole vocabulary. Doing it once at module
 * load is the difference between a linear scan and several hundred passes over
 * every paragraph of a report.
 */
function escape(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
export function buildVocabularyPattern(extra = {}) {
    const lookup = new Map();
    const phrases = [];
    for (const [from, to] of Object.entries(extra)) {
        const key = from.toLowerCase();
        if (lookup.has(key))
            continue;
        lookup.set(key, { from: key, to, confidence: 'certain' });
        phrases.push(key);
    }
    for (const entry of VOCABULARY) {
        if (lookup.has(entry.from))
            continue;
        lookup.set(entry.from, entry);
        phrases.push(entry.from);
    }
    phrases.sort((a, b) => b.length - a.length);
    // Internal whitespace is allowed to be any run of spaces, so "work  cover"
    // and a line break between the words both match.
    const alternation = phrases.map((p) => escape(p).replace(/\\?\s+/g, '\\s+')).join('|');
    const pattern = new RegExp(`(?<![\\w'-])(?:${alternation})(?![\\w'-])`, 'gi');
    return { pattern, lookup };
}
//# sourceMappingURL=vocabulary.js.map